import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getLevantaProductService } from "../../services/products";

const MOBILE_TITLE_LIMIT = 70;

const StarIcon = ({ filled, half }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
  >
    {half && (
      <defs>
        <linearGradient id="pd-star-half">
          <stop offset="50%" stopColor="#FBBF24" />
          <stop offset="50%" stopColor="#D1D5DB" />
        </linearGradient>
      </defs>
    )}
    <path
      d="M8 1.333l1.92 3.894 4.294.624-3.107 3.027.733 4.276L8 10.917l-3.84 2.237.733-4.276L1.786 5.85l4.294-.623L8 1.333z"
      fill={filled ? "#FBBF24" : half ? "url(#pd-star-half)" : "#D1D5DB"}
    />
  </svg>
);

const renderStars = (rating) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) stars.push(<StarIcon key={i} filled />);
    else if (rating >= i - 0.5) stars.push(<StarIcon key={i} half />);
    else stars.push(<StarIcon key={i} />);
  }
  return stars;
};

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="13"
    viewBox="0 0 8 13"
    fill="none"
  >
    <g>
      <path
        d="M0 11.4833L1.49333 13L7.89333 6.5L1.49333 0L0 1.51667L4.90667 6.5L0 11.4833Z"
        fill="#8F9EB0"
      />
    </g>
    <defs>
      <clipPath id="clip0_623_1330">
        <rect
          width="13"
          height="8"
          fill="white"
          transform="matrix(0 -1 1 0 0 13)"
        />
      </clipPath>
    </defs>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path d="M9.89844 3.59998H14.3984V8.09998" stroke="white" />
    <path d="M14.3992 3.59998L7.19922 10.8" stroke="white" />
    <path
      d="M12.6016 11.7V14.4C12.6016 14.6387 12.5067 14.8676 12.338 15.0364C12.1692 15.2052 11.9403 15.3 11.7016 15.3H4.50156C4.26287 15.3 4.03395 15.2052 3.86517 15.0364C3.69638 14.8676 3.60156 14.6387 3.60156 14.4V7.19999C3.60156 6.96129 3.69638 6.73237 3.86517 6.56359C4.03395 6.39481 4.26287 6.29999 4.50156 6.29999H7.20156"
      stroke="white"
    />
  </svg>
);

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [titleExpanded, setTitleExpanded] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await getLevantaProductService(
          new URLSearchParams({ id: productId }).toString(),
        );
        if (data && data.product_title) {
          setProduct(data);
          setActiveImage(data.product_image);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (product) document.title = product.product_title;
  }, [product]);

  if (loading) {
    return (
      <main className="main-wrapper">
        <div className="pd-loading">
          <span className="loader"></span>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="main-wrapper">
        <div className="pd-error">
          <p>Product not found.</p>
          <Link to="/" className="pd-back-link">
            ← Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const {
    product_title,
    product_price,
    product_currency,
    product_category,
    product_image,
    product_in_stock,
    product_rating,
    product_ratings_total,
    brand_name,
    product_link_url,
    product_link_mobile_optimized_url,
  } = product;

  const dealUrl =
    window.innerWidth <= 767 && product_link_mobile_optimized_url
      ? product_link_mobile_optimized_url
      : product_link_url;

  const ratingNum = parseFloat(product_rating) || 0;
  const ratingsTotal = parseInt(product_ratings_total) || 0;

  const needsTruncation = product_title.length > MOBILE_TITLE_LIMIT;
  const truncatedTitle = needsTruncation
    ? product_title.slice(0, MOBILE_TITLE_LIMIT).trimEnd() + "..."
    : product_title;

  return (
    <main className="main-wrapper">
      <div className="pd-wrapper">
        {/* ── Breadcrumb ── */}
        <nav className="pd-breadcrumb">
          <Link to="/" className="pd-breadcrumb-item pd-breadcrumb-link">
            Home
          </Link>
          {product_category && (
            <>
              <span className="pd-breadcrumb-sep">
                <ChevronRight />
              </span>
              <span className="pd-breadcrumb-item">{product_category}</span>
            </>
          )}
        </nav>

        <div className="pd-content">
          {/* ── MOBILE ONLY: brand + truncatable title above the image ── */}
          <div className="pd-mobile-top">
            {brand_name && (
              <div className="pd-brand-badge">Brand: {brand_name}</div>
            )}
            <h1 className="pd-title">
              {titleExpanded ? product_title : truncatedTitle}
              {needsTruncation && (
                <button
                  className="pd-show-more"
                  onClick={() => setTitleExpanded((v) => !v)}
                  type="button"
                >
                  {titleExpanded ? " Show Less" : " Show More"}
                </button>
              )}
            </h1>
          </div>

          {/* ── Gallery ── */}
          <div className="pd-gallery">
            <img
              className="pd-main-image"
              src={activeImage || product_image}
              alt={product_title}
            />
          </div>

          {/* ── Info card ── */}
          <div className="pd-info-card">
            {/* Desktop only: brand + full title inside card */}
            <div className="pd-desktop-top">
              {brand_name && (
                <div className="pd-brand-badge">Brand: {brand_name}</div>
              )}
              <h1 className="pd-title">{product_title}</h1>
            </div>

            {/* Rating */}
            <div className="pd-rating">
              <div className="pd-stars">{renderStars(ratingNum)}</div>
              <span className="pd-rating-value">{ratingNum.toFixed(1)}</span>
              <span className="pd-ratings-count">
                {ratingsTotal.toLocaleString()} ratings
              </span>
            </div>

            {/* Price */}
            <div className="pd-price">
              {product_price} {product_currency}
            </div>

            {/* Availability */}
            <div className="pd-availability">
              {"Availability: "}
              <span
                className={product_in_stock ? "pd-in-stock" : "pd-out-of-stock"}
              >
                {product_in_stock ? "In Stock" : "Out of Stock"}
              </span>
            </div>

            {/* CTA */}
            <div className="pd-deal-wrap">
              <a
                href={dealUrl}
                target="_blank"
                rel="noreferrer"
                className="pd-deal-btn"
              >
                Get This Deal
                <ExternalLinkIcon />
              </a>
            </div>

            {/* Disclaimer */}
            <div className="pd-disclaimer-wrap">
              <p className="pd-disclaimer">
                You’ll continue to the official brand storefront in Amazon.<br />
We may earn a commission.<br />
Read full product details, see more images and read validated customer reviews on the Amazon page by pressing the “Get This Deal” Button 

              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
